import { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { roleToPath } from "../Utils/navigation";
import {
  EntityTypeEnum,
  KYCStatus,
  RegtankOnboardingStatusEnum,
} from "../constant/enum";
import { resetAuthState } from "../features/auth/authSlice_Investor";
import {
  checkIsExpired,
  toISOString,
  sortRegtankRequestsByExpiry,
} from "../Utils/regtankOnboardingHelper";
import { RegtankRequest, UserDetail } from "../Utils/navigation/types";

/**
 * Hook to determine navigation logic after Regtank onboarding
 * Handles modal display decision and navigation based on user's Regtank status
 */
export const useNavigateAfterRegtank = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { redirectPath } = useParams();

  /**
   * Navigate to the default path based on user role
   */
  const navigateToDefaultPath = useCallback(
    (userDetail: { [key: string]: any }) => {
      if (redirectPath) {
        navigate(`/${redirectPath}`);
      } else {
        const path = roleToPath(userDetail);
        if (path) {
          navigate(path);
        } else {
          // Return early without navigating - caller will handle the "no action required" state
          dispatch(resetAuthState());
          return "NO_ACTION_REQUIRED";
        }
      }
    },
    [navigate, dispatch, redirectPath]
  );

  /**
   * Main navigation handler after successful login/2FA
   * Determines whether to show Regtank modal or navigate to app
   *
   * @param userDetail - User details from API
   * @param hasValidOnboardingUrl - Whether there's an existing valid Regtank URL
   * @param openModal - Callback to open the Regtank modal
   */
  const handleNavigateNext = useCallback(
    (
      userDetail: { [key: string]: any },
      hasValidOnboardingUrl: boolean,
      openModal: () => void
    ) => {
      // Check if Regtank onboarding is enabled for this user
      const onRegtankOnboardingFlag =
        userDetail?.regtankOnboardingFeatureFlag &&
        userDetail?.regtankOnboarding;

      const sortedRequests = sortRegtankRequestsByExpiry(
        userDetail?.regtankRequest ?? []
      ) as RegtankRequest[];

      // Check if the user has already done the regtank and approved
      const regtankRequestApproved =
        sortedRequests?.length > 0
          ? sortedRequests?.[0].onboardingStatus ===
            RegtankOnboardingStatusEnum.APPROVED
          : false;

      /**
       * If Regtank is not enabled, navigate to default path
       */
      if (!onRegtankOnboardingFlag) {
        return navigateToDefaultPath(userDetail);
      }

      /**
       * If user is individual without organization or pending resubmission, skip Regtank modal
       */
      const isIndividualNoOrganization =
        userDetail?.entityType === EntityTypeEnum.INDIVIDUAL &&
        (!userDetail?.organization ||
          userDetail?.kycStatus === KYCStatus.PENDING_RESUBMISSION);

      const isIndividualPendingResubmission =
        userDetail?.entityType === EntityTypeEnum.INDIVIDUAL &&
        userDetail?.kycStatus === KYCStatus.PENDING_RESUBMISSION;

      if (
        isIndividualNoOrganization ||
        (isIndividualPendingResubmission && regtankRequestApproved)
      ) {
        return navigateToDefaultPath(userDetail);
      }

      /**
       * Regtank is enabled and user needs verification
       * Check various conditions to determine if modal should be shown
       */

      // Case 1: No Regtank request exists - show modal to start verification
      if (sortedRequests.length === 0) {
        openModal();
        return;
      }

      const latestRequest = sortedRequests[0];

      // Case 2: Has valid onboarding URL that hasn't expired
      if (hasValidOnboardingUrl) {
        openModal();
        return;
      }

      // Case 3: Verification expired - show modal to retry
      if (
        latestRequest.onboardingStatus === RegtankOnboardingStatusEnum.EXPIRED
      ) {
        openModal();
        return;
      }

      // Case 4: Verification is not yet approved but reattempt approved as it maybe rejected or failed
      if (
        latestRequest.onboardingStatus !==
          RegtankOnboardingStatusEnum.APPROVED &&
        latestRequest.reattemptApproval === "APPROVED"
      ) {
        openModal();
        return;
      }

      // Case 5: Verification expired but no updated in Database
      if (
        latestRequest.onboardingStatus ===
          RegtankOnboardingStatusEnum.PROCESSING &&
        checkIsExpired(toISOString(latestRequest.expiredDate))
      ) {
        openModal();
        return;
      }
      /**
       * Default: Navigate to default path
       * (Covers approved, pending, or rejected without reattempt approval)
       */
      return navigateToDefaultPath(userDetail);
    },
    [navigateToDefaultPath]
  );

  return {
    handleNavigateNext,
    navigateToDefaultPath,
  };
};
